import { z } from 'zod';
import { pluralize } from '../pluralize';
// Files a user attaches to a Via chat. The browser keeps each file and what it read from it; the saved chat holds a
// reference to it, and every request carries the content in the reference's place. The server stores nothing.
export const VIA_ATTACHMENT_KINDS = ['pdf', 'docx', 'csv', 'xlsx'];
export const VIA_ATTACHMENT_KIND_LABELS = {
    pdf: 'PDF',
    docx: 'Word document',
    csv: 'CSV file',
    xlsx: 'Excel workbook'
};
const MB = 1024 * 1024;
export const VIA_ATTACHMENT_LIMITS = {
    fileBytes: 10 * MB,
    filesPerMessage: 5,
    filesPerChat: 10,
    /** A PDF this short is sent as the file itself, so Via sees its layout, tables and scanned pages. */
    pdfPagesAsFile: 20,
    /** Longer PDFs are read as text, up to this many pages. */
    pdfPages: 300,
    /** PDFs sent as files travel with every request of the chat, so together they stay under what a request can carry. */
    pdfBytesAsFilePerChat: 8 * MB,
    /** A table this short is shown to Via whole; a longer one as its columns and first rows. */
    tableRowsInFull: 200,
    tableSampleRows: 20,
    /** What Via is given of one file up front; it reads the rest with read_attachment. */
    charsPerFile: 200_000,
    /** About 150k tokens of files in one chat. */
    charsPerChat: 600_000,
    /** What a page of a PDF sent as a file counts for against charsPerChat: about 3k tokens of text and image. */
    charsPerPdfPage: 12_000,
    /** What one read_attachment call returns at most. */
    charsPerRead: 20_000,
    /** The browser forgets a file after this long; Via then asks for it again. */
    keepDays: 30
};
const REFERENCE_PREFIX = 'via-attachment:';
/** What a saved message holds in place of the file. */
export const viaAttachmentReference = (attachmentId) => `${REFERENCE_PREFIX}${attachmentId}`;
/** The file a reference points to, or nothing when the text isn't a reference. */
export const viaAttachmentIdOf = (reference) => reference.startsWith(REFERENCE_PREFIX) ? reference.slice(REFERENCE_PREFIX.length) : undefined;
export const viaAttachmentInfoSchema = z.object({
    attachmentId: z.string().uuid(),
    name: z.string().min(1).max(255),
    kind: z.enum(VIA_ATTACHMENT_KINDS),
    sizeBytes: z.number().int().nonnegative(),
    /** A short PDF goes to Via as the file itself; anything else as the text the browser read from it. */
    sentAs: z.enum(['file', 'text']),
    /** What the file is, for its chip: "12 pages", "3 sheets, 12,430 rows". */
    summary: z.string().max(200),
    /** How much of the chat's allowance (VIA_ATTACHMENT_LIMITS.charsPerChat) the file takes. */
    chars: z.number().int().nonnegative(),
    /** Says what Via was given up front when that isn't the whole file. */
    partial: z.string().max(300).optional()
});
const BASE64_CHARS_PER_BYTE = 4 / 3;
const PDF_BASE64_START = 'JVBERi0';
const referenceSourceSchema = z.object({
    type: z.literal('url'),
    value: z.string().startsWith(REFERENCE_PREFIX)
});
const pdfSourceSchema = z.object({
    type: z.literal('data'),
    mimeType: z.literal('application/pdf'),
    value: z
        .string()
        .max(Math.ceil(VIA_ATTACHMENT_LIMITS.fileBytes * BASE64_CHARS_PER_BYTE) + 4)
        .startsWith(PDF_BASE64_START)
});
const textSourceSchema = z.object({
    type: z.literal('data'),
    mimeType: z.literal('text/plain'),
    value: z.string().max(VIA_ATTACHMENT_LIMITS.charsPerFile)
});
/**
 * An attachment as a request carries it: the PDF itself, the text the browser read from the file, or still the bare
 * reference when the browser no longer has the file.
 */
export const viaAttachmentPartSchema = z.object({
    type: z.literal('document'),
    source: z.union([pdfSourceSchema, textSourceSchema, referenceSourceSchema]),
    metadata: viaAttachmentInfoSchema
});
/** The part a saved message holds for an attached file. */
export const toViaAttachmentPart = (info) => ({
    type: 'document',
    source: { type: 'url', value: viaAttachmentReference(info.attachmentId) },
    metadata: info
});
// Asked of every part of every message on each render, so the cheap test comes first.
export const isViaAttachmentPart = (part) => part.type === 'document' && viaAttachmentPartSchema.safeParse(part).success;
/** The chat's attached files, in the order they were sent. */
export const viaAttachmentsIn = (messages) => messages.flatMap(({ parts }) => parts.filter(isViaAttachmentPart).map(({ metadata }) => metadata));
const pdfBytesOf = ({ source }) => source.type === 'data' && source.mimeType === 'application/pdf'
    ? Math.floor(source.value.length / BASE64_CHARS_PER_BYTE)
    : 0;
/** Why a request's attachments can't be sent, or nothing when they can. The browser keeps to the same limits. */
export const viaAttachmentsProblem = (messages) => {
    const all = messages.flat();
    if (messages.some((parts) => parts.length > VIA_ATTACHMENT_LIMITS.filesPerMessage))
        return `A message can hold ${VIA_ATTACHMENT_LIMITS.filesPerMessage} files at most.`;
    if (all.length > VIA_ATTACHMENT_LIMITS.filesPerChat)
        return `A chat can hold ${VIA_ATTACHMENT_LIMITS.filesPerChat} files at most.`;
    if (all.reduce((bytes, part) => bytes + pdfBytesOf(part), 0) > VIA_ATTACHMENT_LIMITS.pdfBytesAsFilePerChat)
        return 'The PDFs in this chat are too large to send together.';
    if (all.reduce((chars, { metadata }) => chars + metadata.chars, 0) > VIA_ATTACHMENT_LIMITS.charsPerChat)
        return 'The files in this chat are more than Atlas can read at once.';
    return undefined;
};
// A file's name and text come from the user's file, not from the user. Neither may close the element early and pass
// what follows off as something else.
const attribute = (value) => value
    .replace(/[\u0000-\u001f"<>]/g, ' ')
    .trim()
    .slice(0, 120);
const body = (text) => text.replace(/<\/attachment/gi, '<\\/attachment');
const element = ({ attachmentId, name, kind }, attributes, content) => `<attachment id="${attachmentId}" name="${attribute(name)}" type="${VIA_ATTACHMENT_KIND_LABELS[kind]}"${attributes}>\n${content}\n</attachment>`;
/** What Via is given for an attached file: the PDF itself, or the file's text marked off as the file's. */
export const toViaAttachmentModelParts = ({ source, metadata }) => {
    if (source.type === 'url')
        return [
            {
                type: 'text',
                content: element(metadata, ' available="false"', "The user's browser no longer has this file, so you can't read it. If you need it, ask them to attach it again.")
            }
        ];
    if (source.mimeType === 'application/pdf')
        return [
            { type: 'text', content: element(metadata, '', 'The PDF follows.') },
            { type: 'document', source, metadata: { title: attribute(metadata.name) } }
        ];
    return [{ type: 'text', content: element(metadata, '', body(source.value)) }];
};
const READ_MORE = 'Read them with read_attachment.';
const unitHeading = (unit, index) => `## ${unit === 'page' ? 'Page' : 'Part'} ${index + 1}`;
const unitText = (unit, { text }, index) => `${unitHeading(unit, index)}\n\n${text.trim() || `(This ${unit} has no text that can be read; it may be a scan or an image.)`}`;
const renderUnits = ({ unit, units }, from, maxChars) => {
    // Reading again returns the same start, so the note says the rest can't be read rather than how to read it.
    const cutNote = `\n\n(This ${unit} is longer than Atlas can read at once. The rest of it isn't shown and can't be read.)`;
    const shown = [];
    let chars = 0;
    let isCut = false;
    for (const [offset, documentUnit] of units.slice(from).entries()) {
        const text = unitText(unit, documentUnit, from + offset);
        if (shown.length > 0 && chars + text.length > maxChars)
            break;
        // A unit longer than the whole allowance is cut rather than left out, so there's always something to read.
        const fits = text.length <= maxChars;
        shown.push(fits ? text : `${text.slice(0, Math.max(maxChars - cutNote.length, 0))}${cutNote}`);
        isCut ||= !fits;
        chars += text.length + 2;
    }
    return { text: shown.join('\n\n'), to: from + shown.length, isCut };
};
const cell = (value) => value
    .replace(/\|/g, '\\|')
    .replace(/\s*\r?\n\s*/g, ' ')
    .trim();
const tableRow = (label, values, columns) => `| ${label} | ${Array.from({ length: columns }, (_, index) => cell(values[index] ?? '')).join(' | ')} |`;
const columnCount = (rows) => rows.reduce((widest, row) => Math.max(widest, row.length), 0);
/** Rows `from` to `to` (1 is the first row under the header), as a table with each row's number. */
const renderRows = ({ rows }, from, to) => {
    const [header = [], ...data] = rows;
    const columns = columnCount(rows);
    return [
        tableRow('Row', header, columns),
        `| ${Array.from({ length: columns + 1 }, () => '---').join(' | ')} |`,
        ...data.slice(from - 1, to).map((row, index) => tableRow(String(from + index), row, columns))
    ].join('\n');
};
const NUMBER = /^\(?-?[$€£]?\s?(\d{1,3}(,\d{3})+|\d+)?(\.\d+)?%?\)?$/;
const toNumber = (value) => {
    const trimmed = value.trim();
    if (!trimmed || !/\d/.test(trimmed) || !NUMBER.test(trimmed))
        return undefined;
    const number = Number(trimmed.replace(/[^0-9.-]/g, ''));
    if (!Number.isFinite(number))
        return undefined;
    // Accounting sheets write negatives in parentheses.
    return trimmed.startsWith('(') ? -Math.abs(number) : number;
};
const formatNumber = (value) => String(Math.round(value * 100) / 100);
const MOSTLY = 0.9;
const MAX_LISTED_VALUES = 8;
// Totals and ranges come from every row, which Via would otherwise have to guess from the rows it's shown.
const describeColumn = (name, values) => {
    const filled = values.filter((value) => value.trim());
    const label = `- ${cell(name) || '(no header)'}:`;
    if (filled.length === 0)
        return `${label} empty`;
    const blanks = values.length - filled.length;
    const blankNote = blanks > 0 ? `, ${blanks} blank` : '';
    const numbers = filled.flatMap((value) => toNumber(value) ?? []);
    if (numbers.length >= filled.length * MOSTLY) {
        // One pass: a sheet can have more rows than Math.min takes as arguments.
        const { min, max, sum } = numbers.reduce((seen, number) => ({
            min: Math.min(seen.min, number),
            max: Math.max(seen.max, number),
            sum: seen.sum + number
        }), { min: Infinity, max: -Infinity, sum: 0 });
        return `${label} numbers, ${formatNumber(min)} to ${formatNumber(max)}, sum ${formatNumber(sum)}${blankNote}`;
    }
    const counts = new Map();
    filled.forEach((value) => counts.set(value.trim(), (counts.get(value.trim()) ?? 0) + 1));
    if (counts.size > MAX_LISTED_VALUES)
        return `${label} text, ${counts.size} different values${blankNote}`;
    const listed = [...counts.entries()]
        .sort(([, a], [, b]) => b - a)
        .map(([value, count]) => `${cell(value).slice(0, 60)} (${count})`)
        .join(', ');
    return `${label} text, values: ${listed}${blankNote}`;
};
const sheetHeading = ({ name, hidden, rows }) => `## Sheet "${attribute(name)}"${hidden ? ' (hidden in the workbook)' : ''}: ${pluralize(Math.max(rows.length - 1, 0), 'row')}, ${pluralize(columnCount(rows), 'column')}`;
const renderSheet = (sheet) => {
    const dataRows = sheet.rows.length - 1;
    if (dataRows < 1)
        return `${sheetHeading(sheet)}\n\n(No rows.)`;
    if (dataRows <= VIA_ATTACHMENT_LIMITS.tableRowsInFull)
        return `${sheetHeading(sheet)}\n\n${renderRows(sheet, 1, dataRows)}`;
    const [header = [], ...data] = sheet.rows;
    const columns = Array.from({ length: columnCount(sheet.rows) }, (_, index) => describeColumn(header[index] ?? '', data.map((row) => row[index] ?? '')));
    return [
        sheetHeading(sheet),
        `Columns, described from every row:\n${columns.join('\n')}`,
        `Rows 1 to ${VIA_ATTACHMENT_LIMITS.tableSampleRows}:\n\n${renderRows(sheet, 1, VIA_ATTACHMENT_LIMITS.tableSampleRows)}`,
        `(Rows ${VIA_ATTACHMENT_LIMITS.tableSampleRows + 1} to ${dataRows} aren't shown. ${READ_MORE})`
    ].join('\n\n');
};
const ROW_LINE = /^\| \d+ \|/;
/** A sheet too long for its room ends at the last whole row that fits, and says which rows are left. */
const fitSheet = (sheet, maxChars) => {
    const text = renderSheet(sheet);
    if (text.length <= maxChars)
        return { text, isCut: false };
    const kept = text.slice(0, Math.max(text.lastIndexOf('\n', maxChars), 0)).trimEnd();
    const shownRows = kept.split('\n').filter((line) => ROW_LINE.test(line)).length;
    const note = `(Rows ${shownRows + 1} to ${sheet.rows.length - 1} aren't shown. ${READ_MORE})`;
    return { text: kept ? `${kept}\n\n${note}` : note, isCut: true };
};
const countRows = (sheets) => sheets.reduce((total, { rows }) => total + Math.max(rows.length - 1, 0), 0);
/** What the file is, for its chip. */
export const summarizeViaAttachment = (content) => {
    if (content.type === 'document')
        return content.unit === 'page' ? pluralize(content.units.length, 'page') : 'Word document';
    const rows = `${countRows(content.sheets).toLocaleString('en-US')} ${countRows(content.sheets) === 1 ? 'row' : 'rows'}`;
    return content.sheets.length > 1 ? `${pluralize(content.sheets.length, 'sheet')}, ${rows}` : rows;
};
/**
 * The text Via is given for a file up front, and what to tell the user when that isn't all of it. The same file
 * always gives the same text, which keeps the conversation so far in the model's cache.
 */
export const renderViaAttachment = (content, maxChars = VIA_ATTACHMENT_LIMITS.charsPerFile) => {
    if (content.type === 'document') {
        // Room for the note that says what's missing.
        const { text, to, isCut } = renderUnits(content, 0, maxChars - 200);
        const total = content.units.length;
        if (to >= total)
            return { text, ...(isCut && { partial: `Atlas reads the start of a ${content.unit} this long.` }) };
        const units = content.unit === 'page' ? 'Pages' : 'Parts';
        return {
            text: `${text}\n\n(${units} ${to + 1} to ${total} aren't shown. ${READ_MORE})`,
            partial: `Atlas starts with ${units.toLowerCase()} 1 to ${to} of ${total} and looks up the rest when it needs them.`
        };
    }
    // Room for the notes that say what's missing: the rows cut from a sheet, and the name of every sheet that may be
    // left out.
    const room = Math.max(maxChars -
        200 -
        content.sheets.slice(1).reduce((total, { name }) => total + attribute(name).length + 4, 0), 0);
    const sheets = content.sheets.map((sheet) => fitSheet(sheet, room));
    const shown = [];
    let chars = 0;
    for (const sheet of sheets) {
        if (shown.length > 0 && chars + sheet.text.length > room)
            break;
        shown.push(sheet);
        chars += sheet.text.length + 2;
    }
    const hasPartlyShownSheet = shown.some(({ isCut }) => isCut) ||
        content.sheets.some(({ rows }) => rows.length - 1 > VIA_ATTACHMENT_LIMITS.tableRowsInFull);
    const left = content.sheets.slice(shown.length).map(({ name }) => `"${attribute(name)}"`);
    const text = [
        ...shown.map((sheet) => sheet.text),
        ...(left.length > 0 ? [`(Sheets ${left.join(', ')} aren't shown. ${READ_MORE})`] : [])
    ].join('\n\n');
    return {
        text,
        ...((hasPartlyShownSheet || left.length > 0) && {
            partial: 'Atlas starts with the columns and first rows of large sheets and looks up other rows when it needs them.'
        })
    };
};
export const readAttachmentToolSchema = {
    name: 'read_attachment',
    description: "Read more of a file the user attached to this chat: pages of a PDF, parts of a Word document, or rows of a sheet. Use it for what the attachment's text says isn't shown.",
    inputSchema: z.object({
        attachmentId: z.string().describe("The attachment's id"),
        sheet: z.string().optional().describe('The sheet name, for a workbook with more than one sheet'),
        from: z.number().int().min(1).describe('The first page, part or row to read'),
        to: z.number().int().min(1).describe('The last page, part or row to read')
    }),
    outputSchema: z.object({
        name: z.string(),
        text: z.string(),
        shortened: z
            .boolean()
            .describe('The range held more than one call returns; ask again from where the text stops')
    })
};
/** What read_attachment returns for a range of the file. */
export const readViaAttachment = (name, content, { sheet: sheetName, from, to }) => {
    const maxChars = VIA_ATTACHMENT_LIMITS.charsPerRead;
    if (to < from)
        throw new Error('"to" must not be less than "from".');
    if (content.type === 'document') {
        const total = content.units.length;
        if (from > total)
            throw new Error(`The file has ${pluralize(total, content.unit)}; there's no ${content.unit} ${from}.`);
        const last = Math.min(to, total);
        const { text, to: reached } = renderUnits({ ...content, units: content.units.slice(0, last) }, from - 1, maxChars);
        return { name, text: text.slice(0, maxChars), shortened: reached < last || text.length > maxChars };
    }
    const sheet = content.sheets.length === 1 && !sheetName
        ? content.sheets[0]
        : // Via knows a sheet by the name in the text it was given, which has quotes and angle brackets taken out.
            content.sheets.find((candidate) => sheetName !== undefined && attribute(candidate.name) === attribute(sheetName));
    if (!sheet)
        throw new Error(`Say which sheet to read. The workbook's sheets: ${content.sheets.map(({ name: sheetTitle }) => `"${attribute(sheetTitle)}"`).join(', ')}.`);
    const dataRows = Math.max(sheet.rows.length - 1, 0);
    if (from > dataRows)
        throw new Error(`The sheet has ${pluralize(dataRows, 'row')}; there's no row ${from}.`);
    const [header = '', divider = '', ...rows] = renderRows(sheet, from, Math.min(to, dataRows)).split('\n');
    const lines = [header, divider];
    let chars = header.length + divider.length + 1;
    for (const row of rows) {
        // The read ends at the last whole row that fits, so Via can ask again from the next one. A first row too long
        // to fit is given cut short instead: left out, the same request would come back empty every time.
        if (lines.length > 2 && chars + row.length + 1 > maxChars)
            break;
        lines.push(row);
        chars += row.length + 1;
    }
    const text = lines.join('\n');
    return {
        name,
        text: text.slice(0, maxChars),
        shortened: lines.length - 2 < rows.length || text.length > maxChars
    };
};
//# sourceMappingURL=attachments.js.map